import { useState, useRef, MouseEvent, useEffect } from "react";
import cls from "classnames";
import Draggable from "react-draggable";
import { Resizable, ResizeCallbackData } from "react-resizable";
import useStateRef from "logic/src/utils/useStateRef";
import { ReactComponent as CloseIcon } from "logic/src/components/icons/close.svg";
import { ReactComponent as CheckIcon } from "logic/src/components/icons/Check.svg";
import "react-resizable/css/styles.css";
import { ICutBox, ICutBoxFull } from "./types";

import styles from "./cut.module.scss";

interface IDrawBox {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

const getDrawBoxSize = (drawBoxInfo: IDrawBox) => {
  return {
    left: Math.min(drawBoxInfo.endX, drawBoxInfo.startX),
    top: Math.min(drawBoxInfo.endY, drawBoxInfo.startY),
    width: Math.abs(drawBoxInfo.endX - drawBoxInfo.startX),
    height: Math.abs(drawBoxInfo.endY - drawBoxInfo.startY),
  };
};

interface IProps {
  onCut: (v: ICutBoxFull) => void;
}

export const useCut = ({ onCut }: IProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [status, setStatus, statusRef] = useStateRef<
    "init" | "start" | "draw" | "resize"
  >("init");
  const [resizing, setResizing] = useState<boolean>(false);
  const [drawBox, setDrawBox, drawBoxRef] = useStateRef<IDrawBox | null>(null);
  const confirmRef = useRef<() => void>();
  const cancelRef = useRef<() => void>();

  const [box, setBox] = useState<ICutBox>({
    width: 1,
    height: 1,
    top: 0,
    left: 0,
  });

  const handleDrag = (_: any, d: any) => {
    setResizing(true);
    const container = containerRef.current;
    if (container) {
      // const imgRect = img.getBoundingClientRect();
      const newLeft = box.left + d.deltaX;
      const newTop = box.top + d.deltaY;
      if (
        newLeft >= 0 &&
        newLeft <= container.clientWidth - box.width &&
        newTop >= 0 &&
        newTop <= container.clientHeight - box.height
      ) {
        setBox(prev => ({
          ...prev,
          left: newLeft,
          top: newTop,
        }));
      }
    }
  };

  const handleResize = (e: React.SyntheticEvent, data: ResizeCallbackData) => {
    setResizing(true);
    const container = containerRef.current;
    if (container) {
      const newWidth = data.size.width;
      const newHeight = data.size.height;
      if (
        !(
          newWidth >= 20 &&
          newWidth <= container.clientWidth - box.left &&
          newHeight >= 20 &&
          newHeight <= container.clientHeight - box.top
        )
      ) {
        return;
      }
      if (data.handle === "se") {
        setBox(prev => ({
          ...prev,
          width: newWidth,
          height: newHeight,
        }));
      } else if (data.handle === "ne") {
        setBox(prev => {
          const newTop = prev.top + (prev.height - newHeight);
          if (newTop >= 0 && newTop <= container.clientHeight - box.height) {
            return {
              ...prev,
              top: newTop,
              width: newWidth,
              height: newHeight,
            };
          } else {
            return prev;
          }
        });
      } else if (data.handle === "nw") {
        setBox(prev => {
          const newTop = prev.top + (prev.height - newHeight);
          const newLeft = prev.left + (prev.width - newWidth);
          if (
            newLeft >= 0 &&
            newLeft <= container.clientWidth - box.width &&
            newTop >= 0 &&
            newTop <= container.clientHeight - box.height
          ) {
            return {
              left: newLeft,
              top: newTop,
              width: newWidth,
              height: newHeight,
            };
          } else {
            return prev;
          }
        });
      } else if (data.handle === "sw") {
        setBox(prev => {
          const newLeft = prev.left + (prev.width - newWidth);
          if (newLeft >= 0 && newLeft <= container.clientWidth - box.width) {
            return {
              left: newLeft,
              top: prev.top,
              width: newWidth,
              height: newHeight,
            };
          } else {
            return prev;
          }
        });
      }
    }
  };

  const startCut = () => {
    setStatus("start");
  };

  const cancel = () => {
    setStatus("init");
  };

  cancelRef.current = cancel;

  const startDraw = (e: MouseEvent) => {
    if (status === "start") {
      const container = containerRef.current;
      const containerRect = container?.getClientRects();
      setStatus("draw");
      const { clientX, clientY } = e;
      const x = clientX - (containerRect?.[0]?.x || 0);
      const y = clientY - (containerRect?.[0]?.y || 0);

      setDrawBox({
        startX: x,
        startY: y,
        endX: x,
        endY: y,
      });
    }
  };

  const endDraw = (e: MouseEvent) => {
    setResizing(false);
    if (status === "draw") {
      const container = containerRef.current;
      const containerRect = container?.getClientRects();
      const { clientX, clientY } = e;
      const x = clientX - (containerRect?.[0]?.x || 0);
      const y = clientY - (containerRect?.[0]?.y || 0);
      setStatus("resize");
      const drawBoxInfo = drawBoxRef.current!;
      setBox(getDrawBoxSize({ ...drawBoxInfo, endX: x, endY: y }));
      setDrawBox(null);
    }
  };

  const draw = (e: MouseEvent) => {
    const container = containerRef.current;
    const containerRect = container?.getClientRects();

    if (status === "draw") {
      const { clientX, clientY } = e;
      setDrawBox((pre: IDrawBox | null) => ({
        ...pre!,
        endX: clientX - (containerRect?.[0]?.x || 0),
        endY: clientY - (containerRect?.[0]?.y || 0),
      }));
    }
  };
  const drawBoxSize = drawBoxRef.current
    ? getDrawBoxSize(drawBoxRef.current)
    : undefined;

  const confirm = () => {
    const container = containerRef.current;
    const containerRect = container?.getClientRects();
    const x = box.left + (containerRect?.[0]?.x || 0);
    const y = box.top + (containerRect?.[0]?.y || 0);
    console.log({
      ...box,
      x,
      y,
      endX: x + box.width,
      endY: y + box.height,
    });
    onCut({
      ...box,
      x,
      y,
      endX: x + box.width,
      endY: y + box.height,
    });
    setStatus("init");
  };

  confirmRef.current = confirm;

  const handleDoubleClick = () => {
    if (status === "resize") {
      confirm();
    }
  };

  useEffect(() => {
    const listKey = (e: KeyboardEvent) => {
      if (statusRef.current === "resize") {
        if (e.code === "Enter") {
          e.preventDefault();
          confirmRef.current?.();
        } else if (e.code === "Escape") {
          e.preventDefault();
          cancelRef.current?.();
        }
      }
    };

    document.addEventListener("keydown", listKey);
    return () => document.removeEventListener("keydown", listKey);
  }, []);

  const handler =
    status !== "init" ? (
      <div
        ref={containerRef}
        className={cls(
          styles.container,
          status === "start" ? styles.mask : undefined,
        )}
        onDoubleClick={handleDoubleClick}
        onMouseDown={startDraw}
        onMouseUp={endDraw}
        onMouseMove={draw}
      >
        {drawBox && <div className={styles.drawBox} style={drawBoxSize}></div>}
        {status === "resize" && (
          <Draggable
            position={{ x: box.left, y: box.top }}
            onDrag={handleDrag}
            bounds="parent"
            cancel=".react-resizable-handle"
          >
            <Resizable
              width={box.width}
              height={box.height}
              minConstraints={[20, 20]}
              maxConstraints={[
                containerRef.current?.clientWidth || 800,
                containerRef.current?.clientHeight || 600,
              ]}
              handleSize={[4, 4]}
              resizeHandles={["se", "ne", "nw", "sw"]}
              onResize={handleResize}
              className="absolute top-0 border-2 border-blue-500 bg-opacity-20 bg-blue-200"
            >
              <div
                style={{ width: box.width + "px", height: box.height + "px" }}
                ref={boxRef}
                className={styles.innerBox}
              >
                {!resizing && (
                  <div className={styles.operationBox}>
                    <CloseIcon onClick={cancel} className={styles.closeIcon} />
                    <CheckIcon onClick={confirm} className={styles.checkIcon} />
                  </div>
                )}
              </div>
            </Resizable>
          </Draggable>
        )}
      </div>
    ) : null;

  return {
    handler,
    startCut,
  };
};
